import { useMemo } from 'react'

interface Props {
  values: number[]
  width?: number
  height?: number
  tone?: 'good' | 'bad' | 'neutral'
}

export function Sparkline({ values, width = 140, height = 36, tone = 'good' }: Props) {
  const path = useMemo(() => {
    if (values.length < 2) return ''
    const min = Math.min(...values)
    const max = Math.max(...values)
    const stepX = width / (values.length - 1)
    // Když se hodnota za celé období nezměnila, není co škálovat — čára
    // patří doprostřed, ne přilepená ke spodní hraně.
    if (max === min) {
      return `M0,${(height / 2).toFixed(2)} L${width},${(height / 2).toFixed(2)}`
    }
    const range = max - min
    return values
      .map((v, i) => {
        const x = (i * stepX).toFixed(2)
        const y = (height - ((v - min) / range) * height).toFixed(2)
        return `${i === 0 ? 'M' : 'L'}${x},${y}`
      })
      .join(' ')
  }, [values, width, height])

  const stroke =
    tone === 'good'
      ? 'var(--signal-good)'
      : tone === 'bad'
        ? 'var(--signal-bad)'
        : 'var(--brand-accent)'

  if (!path) return <span aria-hidden="true" className="block size-full" />

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="size-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`sparkfill-${tone}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.32" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={`${path} L${width},${height} L0,${height} Z`}
        fill={`url(#sparkfill-${tone})`}
        stroke="none"
      />
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinecap="round" />
    </svg>
  )
}
